import React, { useEffect, useRef, useState } from 'react';
import { Trash } from '@saleshandy/icons';

import type { IProps } from './do-not-call-list-details-table-container';

import SelectedPhoneNumbersInfoBanner from './selected-phone-numbers-info-banner';
import Table from '../../../../../../../../shared/design-system/components/organisms/table';
import { PaginationShowHide } from '../../../../../../../../shared/design-system/components/organisms/table/types';
import { getDoNotCallListDetailsColumns } from './utils/get-do-not-call-list-details-columns';
import { constants } from '../../../../../../../../shared/enums/constants';
import { executeOnRequestStatus } from '../../../../../../../../shared/utils';
import hasPermission from '../../../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../../../shared/utils/access-control/enums/permissions';
const BULK_DELETE_LIMIT = constants.BULK_DELETE_DNC_LIST_EMAILS_DOMAINS;

const DoNotCallListDetailsTable: React.FC<IProps> = ({
  doNotCallListDetails,
  doNotCallListDetailsPaginationOptions,
  filters,
  onFiltersChange,
  onAction,
  isLoading,
  getDoNotCallListDetailsRequestStatus,

  resetSelected,
  handleResetSelected,
  bulkSelectedPhoneNumbers,
  selectedPhoneNumbers,
  clearPhoneNumbersSelection,
  onBulkSelectPhoneNumbers,
  selectAllPhoneNumbers,
  selectSinglePhoneNumber,
}) => {
  const tableRef = useRef<any>(null);
  const [isBulkSelected, setIsBulkSelected] = useState<boolean>(false);

  const onPaginationOptionsChange = ({ page, limit }) => {
    if (filters.page === page && filters.limit === limit) {
      return;
    }
    onFiltersChange({ page, limit });
  };

  const handleSelectDeselectRows = (rows: any, status: boolean) => {
    selectAllPhoneNumbers({ rows, status });
    setIsBulkSelected(false);
  };

  const handleSelectAllRows = () => {
    const lastPage =
      BULK_DELETE_LIMIT / doNotCallListDetailsPaginationOptions?.itemsPerPage;

    if (
      doNotCallListDetailsPaginationOptions?.currentPage <
      Math.ceil(doNotCallListDetailsPaginationOptions?.totalItems / doNotCallListDetailsPaginationOptions?.itemsPerPage)
    ) {
      tableRef?.current?.selectAllCurrentPageRows();
    } else {
      tableRef?.current?.resetSelectedRows();
    }

    onBulkSelectPhoneNumbers({
      isAllPhoneNumbersSelected: true,
      deSelectedPhoneNumberIds: [],
    });
  };

  const handleClearSelection = (resetSelectedRows = false) => {
    clearPhoneNumbersSelection();
    handleSelectDeselectRows(doNotCallListDetails, false);

    if (resetSelectedRows) {
      tableRef.current?.resetSelectedRows();
    }
  };

  const handleSelectSingleRow = (row: any, status: boolean) => {
    selectSinglePhoneNumber({ row, status });
    setIsBulkSelected(false);
  };

  const actions = [
    {
      key: 'delete',
      iconElement: <Trash />,
      displayName: 'Delete',
      position: 'out',
    },
  ];

  useEffect(() => {
    executeOnRequestStatus({
      status: getDoNotCallListDetailsRequestStatus,
      onSuccess: () => {
        if (bulkSelectedPhoneNumbers?.isAllPhoneNumbersSelected) {
          tableRef.current?.selectAllCurrentPageRows();
        }
      },
    });
  }, [getDoNotCallListDetailsRequestStatus]);

  useEffect(
    () => () => {
      handleClearSelection();
    },
    [],
  );

  const showBanner =
    selectedPhoneNumbers?.length > 0 ||
    bulkSelectedPhoneNumbers?.isAllPhoneNumbersSelected;

  return (
    <div className="do-not-contact-list-table-wrapper">
      {showBanner && (
        <div className="email-account-banner">
          <SelectedPhoneNumbersInfoBanner
            selectedPhoneNumbers={selectedPhoneNumbers}
            bulkSelectedPhoneNumbers={bulkSelectedPhoneNumbers}
            totalPhoneNumbers={
              doNotCallListDetailsPaginationOptions?.totalItems
            }
            handleSelectAllRows={handleSelectAllRows}
            handleClearSelection={() => handleClearSelection(true)}
          />
        </div>
      )}

      <Table
        columns={getDoNotCallListDetailsColumns()}
        data={doNotCallListDetails}
        {...(hasPermission(Permissions.DNC_LIST_DELETE) && {
          actionColumnName: 'Action',
          actions,
          onAction,
        })}
        paginationOptions={{
          options: {
            limit: doNotCallListDetailsPaginationOptions?.itemsPerPage,
            page: doNotCallListDetailsPaginationOptions?.currentPage,
            totalElements: doNotCallListDetailsPaginationOptions?.totalItems,
          },
        }}
        tableWrapperClasses="do-not-contact-list-details-table"
        isLoading={isLoading}
        pagination={PaginationShowHide.SHOW}
        isNewPagination={true}
        onPaginationOptionsChange={onPaginationOptionsChange}
        headerVisibleForGenerateColumn={true}
        onRowSelect={handleSelectSingleRow}
        onRowSelectAll={handleSelectDeselectRows}
        resetSelected={resetSelected}
        handleResetSelected={handleResetSelected}
        deselectedRows={bulkSelectedPhoneNumbers?.deSelectedPhoneNumberIds}
        tableRef={(refValue: any) => {
          tableRef.current = refValue;
        }}
      />
    </div>
  );
};

export default DoNotCallListDetailsTable;
